import { useCallback, useRef, useSyncExternalStore } from 'react';

type Parser<T> = {
  parse: (value: string) => T | null;
  serialize: (value: T) => string;
  defaultValue?: T;
  withDefault: (defaultValue: T) => ParserWithDefault<T>;
};

// A parser that's had `.withDefault()` applied always yields a non-null value,
// so `useQueryState`/`useQueryStates` can return `T` instead of `T | null` for it.
type ParserWithDefault<T> = Parser<T> & { defaultValue: T };

function createParser<T>(parse: (value: string) => T | null, serialize: (value: T) => string): Parser<T> {
  const parser: Parser<T> = {
    parse,
    serialize,
    withDefault(defaultValue: T) {
      return { ...parser, defaultValue };
    }
  };
  return parser;
}

export const parseAsString = createParser<string>(
  (v) => v,
  (v) => v
);

export const parseAsBoolean = createParser<boolean>(
  (v) => v === 'true',
  (v) => (v ? 'true' : 'false')
);

export const parseAsInteger = createParser<number>(
  (v) => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? null : n;
  },
  (v) => String(v)
);

const listeners = new Set<() => void>();
let version = 0;

const notify = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

if (typeof window !== 'undefined') {
  window.addEventListener('popstate', notify);
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const getSearchParams = () => new URLSearchParams(window.location.search);

const writeParams = (updates: Record<string, string | null>) => {
  const params = getSearchParams();
  Object.entries(updates).forEach(([key, value]) => {
    if (value === null) params.delete(key);
    else params.set(key, value);
  });
  const search = params.toString();

  if (search === getSearchParams().toString()) return;
  const query = search ? `?${search}` : '';
  const url = `${window.location.pathname}${query}${window.location.hash}`;
  window.history.replaceState(window.history.state, '', url);
  notify();
};

const readValue = <T>(params: URLSearchParams, key: string, parser: Parser<T>): T | null => {
  const raw = params.get(key);
  if (raw === null) return parser.defaultValue ?? null;
  const parsed = parser.parse(raw);
  return parsed ?? parser.defaultValue ?? null;
};

type SetQueryState<T> = (value: T | null | ((prev: T | null) => T | null)) => void;

export function useQueryState<T>(key: string, parser: ParserWithDefault<T>): [T, SetQueryState<T>];
export function useQueryState<T>(key: string, parser: Parser<T>): [T | null, SetQueryState<T>];
export function useQueryState<T>(key: string, parser: Parser<T>): [T | null, SetQueryState<T>] {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version
  );

  const value = readValue(getSearchParams(), key, parser);

  const parserRef = useRef(parser);
  parserRef.current = parser;

  const setValue = useCallback<SetQueryState<T>>(
    (next) => {
      const current = readValue(getSearchParams(), key, parserRef.current);
      const resolved = typeof next === 'function' ? (next as (prev: T | null) => T | null)(current) : next;
      writeParams({ [key]: resolved === null ? null : parserRef.current.serialize(resolved) });
    },
    [key]
  );

  return [value, setValue];
}

type ParsedValue<P> = P extends ParserWithDefault<infer T> ? T : P extends Parser<infer T> ? T | null : never;

type ParsedSchema<S extends Record<string, Parser<any>>> = { [K in keyof S]: ParsedValue<S[K]> };

export function useQueryStates<S extends Record<string, Parser<any>>>(
  schema: S
): [ParsedSchema<S>, (updates: Partial<{ [K in keyof S]: ParsedSchema<S>[K] }>) => void] {
  useSyncExternalStore(
    subscribe,
    () => version,
    () => version
  );

  const params = getSearchParams();
  const values = Object.fromEntries(
    Object.entries(schema).map(([key, parser]) => [key, readValue(params, key, parser)])
  ) as ParsedSchema<S>;

  const schemaRef = useRef(schema);
  schemaRef.current = schema;

  const setValues = useCallback((updates: Partial<Record<string, unknown>>) => {
    const serialized: Record<string, string | null> = {};
    Object.entries(updates).forEach(([key, value]) => {
      const parser = schemaRef.current[key];
      serialized[key] = value === null || value === undefined ? null : parser.serialize(value);
    });
    writeParams(serialized);
  }, []);

  return [values, setValues];
}
